import PropTypes from 'prop-types';
import isNil from 'lodash/isNil';
import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import { CollapseItem } from '../../../common/components/Collapse';
import Recipient from '../../../common/models/Recipient';
import Participation from '../../../common/models/Participation';
import useProjectVariables from '../../../utils/useProjectVariables';
import Button from '../../Button';
import { default as ProjectSelect } from '../../../common/selectors/Project';
import { apiZedocOneProject } from '../../../common/api/zedoc';

const RecipientVariablesList = ({
  recipient,
  participation,
  onEdit,
  canEdit,
}) => {
  const { t, i18n } = useTranslation();

  const { _id: recipientId } = recipient || {};
  const { projectId, _id: participationId } = participation || {};
  const { variables, variablesReady } = useProjectVariables(projectId);

  const { ready: projectReady } = useDDPSubscription(
    projectId &&
      apiZedocOneProject.withParams({
        projectId,
      }),
  );

  const details = [];

  variables.forEach((variable) => {
    if (!variable.isPatient() && !variable.isParticipation()) {
      return;
    }

    if (variable.name === 'xml_metadata') {
      return;
    }

    const value = variable.getFromContext({
      recipient,
      participation,
    });

    if (value !== '' && !isNil(value)) {
      // FIXME: This is a temporary solution to mask the national health identifier
      // TECH-2473 requires masking the national health identifier
      const mask = variable.name === 'National health identifier';
      details.push({
        variableId: variable._id,
        title: variable.name,
        description: mask
          ? '*******'
          : variable.getDisplayValue(value, {
              projectId,
              language: i18n.language,
              useDateTimeFormat: true,
            }),
      });
    }
  });

  const project = useSelector(ProjectSelect.one().whereIdEquals(projectId));
  if (!projectReady || !variablesReady) {
    return null;
  }

  const handleOnEdit = () =>
    onEdit({ recipientId, participationId, projectId });

  const content = (
    <div className="cluster-6 justify-between items-center">
      <div className="cluster-6 flex-wrap items-center">
        {details.map(({ variableId, title, description }, i) => (
          <React.Fragment key={variableId}>
            {i > 0 && <div>&#xb7;</div>}
            <div className="stack-0">
              <p className="font-medium">{title}</p>
              <p className="break-all">{description}</p>
            </div>
          </React.Fragment>
        ))}
      </div>
      {canEdit && (
        <Button type="ghost" onClick={handleOnEdit} className="flex-shrink-0">
          {t('edit')}
        </Button>
      )}
    </div>
  );

  return (
    <CollapseItem
      title={`${project && project.getName()} - ${
        participation &&
        participation.state &&
        t(`states:${participation.state.toLowerCase()}`)
      }`}
      content={content}
    />
  );
};

RecipientVariablesList.propTypes = {
  recipient: PropTypes.instanceOf(Recipient),
  participation: PropTypes.instanceOf(Participation),
  onEdit: PropTypes.func,
  canEdit: PropTypes.bool,
};

RecipientVariablesList.defaultProps = {
  recipient: null,
  participation: null,
  onEdit: () => {},
  canEdit: false,
};

export default RecipientVariablesList;
